import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { usePatchTicketMutation, useRetrieveTicketQuery } from '@sophocles/features/entities/tickets';
import { addToast } from '@sophocles/features/toast';
import { parseApiError } from '@sophocles/utils/apiErrors';

import EditTicketForm from './forms/EditTicketForm';

export default function EditTicketView(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ticket_id: ticketId } = useParams();
  const [patchTicket] = usePatchTicketMutation();
  const [nonFieldMessages, setNonFieldMessages] = React.useState([]);

  const { data: ticket, isLoading, error } = useRetrieveTicketQuery({ id: ticketId }, { skip: !ticketId });
  const ticketNumber = ticket?.ticket_number ?? '';

  const handleSubmit = async (id, values) => {
    setNonFieldMessages([]);
    const result = await patchTicket({ id, body: values });

    if (result.error) {
      setNonFieldMessages(
        parseApiError(result.error, {
          knownFields: [],
          genericMessage: t('tickets.ticketUpdateFailed', 'Failed to update ticket. Please try again.'),
        }).nonFieldMessages,
      );
      return;
    }

    dispatch(
      addToast({
        title: t('general.success', 'Success'),
        message: t('tickets.ticketUpdated', 'Ticket Updated'),
        level: 'success',
        dismissible: true,
      }),
    );
    navigate(-1);
  };

  const pageTitle = ticketNumber
    ? `${t('pages.editSupportTicket', 'Edit Ticket')} - ${ticketNumber}`
    : t('pages.editSupportTicket', 'Edit Ticket');

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error || !ticket) {
    return (
      <AppNav notFullHeight page="plant_tickets" {...props}>
        <Box sx={{ padding: '0 var(--page-px) var(--page-py)' }}>
          <TitleBar showBackButton title={pageTitle} />
          <Typography color="error">{t('tickets.ticketLoadFailed', 'Failed to load ticket')}</Typography>
        </Box>
      </AppNav>
    );
  }

  return (
    <AppNav notFullHeight page="plant_tickets" {...props}>
      <Box sx={{ padding: '0 var(--page-px) var(--page-py)' }}>
        <TitleBar showBackButton title={pageTitle} />
        <EditTicketForm
          ticket={ticket}
          onSubmit={handleSubmit}
          onCancel={() => navigate(-1)}
          errorMessages={nonFieldMessages}
        />
      </Box>
    </AppNav>
  );
}
